import React from 'react';
import { pdf } from '@react-pdf/renderer';
import ThermalBillPDF from './ThermalBillPdf.tsx';
import endpoint from '../config/endpoints';
import { noInternetMsg, serverMsg } from '../config/messageconstant';
import { apiPost } from './api_service';

// Maps a bill from /report-by-ids (same response Bill PDF uses) to the
// shape ThermalBillPDF expects.
const mapBillForThermalPdf = (bill) => {
  const toBill = bill.outLet ? bill.outLet : {};
  const billDetails = bill.outletBillDetails ? bill.outletBillDetails : [];

  const items = billDetails.map((stockDetails) => {
    const gstPer =
      (stockDetails.cgstPer || 0) +
      (stockDetails.sgstPer || 0) +
      (stockDetails.igstPer || 0);
    // Same rate as Bill PDF: taxable rate only for GST type 3, else the bill rate
    const rate =
      bill.stockStakeHolderGstType == 3 &&
      stockDetails.totalAmt !== 0 &&
      stockDetails.qty !== 0
        ? Number(((stockDetails.totalAmt / stockDetails.qty) * 100).toFixed(2)) /
          (100 + gstPer)
        : stockDetails.rate || 0;
    return {
      name: stockDetails.product ? stockDetails.product.name : '',
      hsnCode: stockDetails.hsnCode,
      uomName: stockDetails.uom ? stockDetails.uom.name : '',
      qty: stockDetails.qty || 0,
      rate: Number(rate) || 0,
      amount: stockDetails.totalAmt || 0,
    };
  });

  // Group line items by GST slab for the tax breakdown table
  const taxGroups = {};
  billDetails.forEach((stockDetails) => {
    const key = `${stockDetails.cgstPer || 0}-${stockDetails.sgstPer || 0}-${stockDetails.igstPer || 0}`;
    if (!taxGroups[key]) {
      taxGroups[key] = {
        cgstPer: stockDetails.cgstPer || 0,
        sgstPer: stockDetails.sgstPer || 0,
        taxableAmt: 0,
        cgstAmt: 0,
        sgstAmt: 0,
      };
    }
    const group = taxGroups[key];
    const cgstAmt = stockDetails.cgstAmt || 0;
    const sgstAmt = stockDetails.sgstAmt || 0;
    const igstAmt = stockDetails.igstAmt || 0;
    const totalAmt = stockDetails.totalAmt || 0;
    group.taxableAmt += totalAmt - cgstAmt - sgstAmt - igstAmt;
    group.cgstAmt += cgstAmt;
    group.sgstAmt += sgstAmt;
  });
  const taxBreakdown = Object.values(taxGroups).map((group, index) => ({
    ...group,
    index: index + 1,
  }));

  const totalAmt = billDetails.reduce(
    (sum, stockDetails) => sum + (stockDetails.totalAmt || 0),
    0,
  );

  return {
    invoiceNo: bill.billNo || '-',
    insertDateTime: bill.billDate || bill.insertDateTime,
    employeeName: bill.salesTeamNames,
    remark: bill.orderRemarks,
    customer: {
      name: toBill.firmName || '',
      phone: toBill.mobileNo,
    },
    company: {
      name: bill.stockStakeHolderFirmName || '',
      address: bill.stockStakeHolderAddress || '',
      phone: bill.stockStakeHolderContactPersonNo || '',
      gstin: toBill.gstNo,
      fssai: bill.stockStakeHolderFssiFdiNo,
    },
    items,
    taxBreakdown,
    totalAmt,
    payableAmt: totalAmt,
  };
};

// Fetches bills by id (same /report-by-ids call Bill PDF uses) and opens
// them as a thermal receipt PDF in a new tab. `ids` is a comma-separated
// string or a single id.
export const openThermalBillPdf = ({ ids, lang, showLoador, showNotification }) => {
  if (!navigator.onLine) {
    showNotification({ msg: noInternetMsg, severity: 'error' });
    return;
  }

  // Open the tab synchronously so the browser doesn't block it as a popup
  const pdfWindow = window.open('', '_blank');

  let billPdfData = {
    outletBillHeaderIds: String(ids).split(','),
    languageId: lang,
  };
  showLoador({ loador: true });
  apiPost({
    url: endpoint.outletBillHeader + '/report-by-ids',
    postBody: billPdfData,
  }).then(async ({ data, success }) => {
    if (!success || !data.data || data.data.length == 0) {
      showLoador({ loador: false });
      if (pdfWindow) pdfWindow.close();
      showNotification({
        msg: serverMsg,
        severity: 'error',
      });
      return;
    }
    try {
      const thermalData = data.data.map(mapBillForThermalPdf);
      const blob = await pdf(<ThermalBillPDF data={thermalData} />).toBlob();
      const pdfUrl = URL.createObjectURL(blob);
      if (pdfWindow) {
        pdfWindow.location.href = pdfUrl;
      } else {
        window.open(pdfUrl, '_blank');
      }
    } catch (error) {
      console.error('Thermal bill PDF error:', error);
      if (pdfWindow) pdfWindow.close();
      showNotification({
        msg: serverMsg,
        severity: 'error',
      });
    } finally {
      showLoador({ loador: false });
    }
  });
};
